import type {
    CreateExtArgs,
    CreateExtension,
    DmApis,
    ExtensionAPI,
    PostTransactionOperation
} from '@wix/document-manager-core'
import type {DocumentServicesModelExtApi} from '../documentServicesModel'
import type {CSaveApi} from './continuousSave'

type RevisionChangeCb = (newRevision: number, previousRevision: number) => void

export interface RevisionManagerExtApi extends ExtensionAPI {
    revisionManager: {
        registerToRevisionChange(cb: RevisionChangeCb): void
        unregisterFromRevisionChange(cb: RevisionChangeCb): void
    }
}

const createExtension: CreateExtension = () => {
    let currentRevision: number
    const revisionChangeCallbacks: Set<RevisionChangeCb> = new Set()

    const registerToRevisionChange = (cb: RevisionChangeCb): void => {
        revisionChangeCallbacks.add(cb)
    }

    const unregisterFromRevisionChange = (cb: RevisionChangeCb): void => {
        revisionChangeCallbacks.delete(cb)
    }

    const notifyRevisionChanged = (newRevision: number, previousRevision: number): void => {
        revisionChangeCallbacks.forEach(cb => {
            cb(newRevision, previousRevision)
        })
    }

    return {
        name: 'revisionManager',
        dependencies: new Set(['documentServicesModel', 'continuousSave']),
        async initialize(dmApis: DmApis): Promise<void> {
            const {siteAPI} = dmApis.extensionAPI as DocumentServicesModelExtApi
            currentRevision = siteAPI.getSiteRevision()
        },
        createExtensionAPI(): RevisionManagerExtApi {
            return {
                revisionManager: {
                    registerToRevisionChange,
                    unregisterFromRevisionChange
                }
            }
        },
        createPublicAPI({extensionAPI}: DmApis): RevisionManagerExtApi {
            const {revisionManager} = extensionAPI as RevisionManagerExtApi
            return {
                revisionManager: {
                    registerToRevisionChange(cb: RevisionChangeCb): void {
                        revisionManager.registerToRevisionChange(cb)
                    },
                    unregisterFromRevisionChange(cb: RevisionChangeCb): void {
                        revisionManager.unregisterFromRevisionChange(cb)
                    }
                }
            }
        },
        createPostTransactionOperations(createExtArgs: CreateExtArgs): Record<string, PostTransactionOperation> {
            return {
                revisionChange: () => {
                    const {siteAPI} = createExtArgs.extensionAPI as DocumentServicesModelExtApi
                    const newRevision = siteAPI.getSiteRevision()
                    if (currentRevision !== undefined && newRevision !== currentRevision) {
                        const previousRevision = currentRevision
                        currentRevision = newRevision
                        const {continuousSave} = createExtArgs.extensionAPI as CSaveApi
                        continuousSave.onRevisionChange()
                        notifyRevisionChanged(newRevision, previousRevision)
                    }
                }
            }
        }
    }
}

export {createExtension}
